import { motion } from 'framer-motion'
import type { Exercise } from '../../types'
import { getSharedMuscleGroups } from '../../lib/exerciseSwap'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Panel } from '../../shared/ui/Panel'

interface ExerciseSwapModalProps {
  isOpen: boolean
  currentExerciseName: string
  targetMuscleGroups?: string[]
  options: Exercise[]
  loading?: boolean
  saving?: boolean
  error?: string | null
  onClose: () => void
  onSwap: (exercise: Exercise) => void
}

export function ExerciseSwapModal({
  isOpen,
  currentExerciseName,
  targetMuscleGroups,
  options,
  loading = false,
  saving = false,
  error,
  onClose,
  onSwap,
}: ExerciseSwapModalProps) {
  return (
    <Panel
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={`Swap ${currentExerciseName}`}
      variant="modal"
      size="sm"
    >
      <p className="text-sm text-gray-600 dark:text-slate-400 -mt-2 mb-4">
        This only changes the current session.
      </p>

      {targetMuscleGroups && targetMuscleGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {targetMuscleGroups.map((group) => (
            <Badge key={group} variant="primary" size="sm">
              {group}
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-10 text-sm text-gray-600 dark:text-slate-400">
            Loading alternatives...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-4 text-sm text-amber-800 backdrop-blur-xl dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            {error}
          </div>
        )}

        {!loading && !error && options.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white/40 px-4 py-8 text-center text-sm text-gray-600 backdrop-blur-xl dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            No matching alternatives found for this exercise.
          </div>
        )}

        {!loading && !error && options.map((exercise) => {
          const sharedGroups = getSharedMuscleGroups(targetMuscleGroups, exercise.targetMuscleGroups)

          return (
            <motion.button
              key={exercise.id}
              type="button"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => onSwap(exercise)}
              disabled={saving}
              className="w-full rounded-2xl border border-gray-200 bg-white/55 p-4 text-left shadow-sm transition hover:border-primary-300 hover:bg-white/75 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:hover:border-primary-500/40 dark:hover:bg-white/10"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      {exercise.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {exercise.description || 'No description yet.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sharedGroups.map((group) => (
                      <Badge key={`${exercise.id}-${group}`} variant="success" size="sm">
                        {group}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Badge variant="info" size="sm">
                  Exercise
                </Badge>
              </div>
            </motion.button>
          )
        })}
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Close
        </Button>
      </div>
    </Panel>
  )
}
